import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { format, startOfWeek, addDays, getISOWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarBlank, Plus, X, FilePdf, ListBullets, CalendarDots, GoogleLogo } from '@phosphor-icons/react';
import { PlanningTaskModal } from '../components/planning/PlanningModals';
import { AnnualCalendarView } from '../components/planning/AnnualCalendar';
import { PlanningWeekView } from '../components/planning/PlanningWeekView';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useGoogleSync } from '../hooks/useGoogleSync';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const initialFormData = () => ({
  user_id: '', user_ids: [], date_mode: 'single',
  date: format(new Date(), 'yyyy-MM-dd'),
  start_date: '', end_date: '', weekdays: [],
  start_time: '08:00', end_time: '17:00', title: '', description: '', address: '',
  color: '#1565C0', priority: 'normal',
});

const PlanningPage = () => {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [viewMode, setViewMode] = useState('week'); // 'week' | 'year'
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [yearTasks, setYearTasks] = useState([]);
  const [yearLoading, setYearLoading] = useState(false);
  const [hoveredDay, setHoveredDay] = useState(null);
  const [holidaysByDate, setHolidaysByDate] = useState({});
  const [includeAdmins, setIncludeAdmins] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  const weekStartStr = format(weekStart, 'yyyy-MM-dd');
  const weekEndStr = format(addDays(weekStart, 6), 'yyyy-MM-dd');

  // Google Calendar sync (admin only)
  const { status: googleStatus, events: googleEvents, connect: handleGoogleConnect, disconnect: handleGoogleDisconnect } =
    useGoogleSync(isAdmin, weekStartStr, weekEndStr);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const year = weekStart.getFullYear();
      const employeesEndpoint = (isAdmin && includeAdmins) ? '/api/users/all-resources' : '/api/users/employees';
      const [tasksRes, employeesRes, projectsRes, holidaysRes] = await Promise.all([
        axios.get(`${API_URL}/api/planning/week?week_start=${weekStartStr}`, { withCredentials: true }),
        axios.get(`${API_URL}${employeesEndpoint}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects`, { withCredentials: true }),
        axios.get(`${API_URL}/api/planning/holidays?year=${year}`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setTasks(tasksRes.data);
      setEmployees(employeesRes.data);
      setProjects(projectsRes.data);
      const hmap = {};
      (holidaysRes.data || []).forEach(h => { hmap[h.date] = h.name; });
      setHolidaysByDate(hmap);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [weekStartStr, weekStart, isAdmin, includeAdmins]);

  const fetchYearTasks = useCallback(async () => {
    setYearLoading(true);
    try {
      const start = `${selectedYear}-01-01`;
      const end = `${selectedYear}-12-31`;
      const res = await axios.get(`${API_URL}/api/planning?start_date=${start}&end_date=${end}`, { withCredentials: true });
      setYearTasks(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement annuel');
    } finally {
      setYearLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (viewMode === 'year') fetchYearTasks(); }, [viewMode, fetchYearTasks]);

  // Auto-refresh when tab regains focus (catches name/photo edits in another tab)
  // + silent polling every 60s while the page is visible.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') fetchData(); };
    document.addEventListener('visibilitychange', onVisible);
    const t = setInterval(() => { if (document.visibilityState === 'visible') fetchData(); }, 60000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(t);
    };
  }, [fetchData]);

  const yearTaskMap = useMemo(() => {
    const map = {};
    yearTasks.forEach(t => {
      if (!map[t.date]) map[t.date] = [];
      map[t.date].push(t);
    });
    return map;
  }, [yearTasks]);

  const resetForm = () => setFormData(initialFormData());

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editTask) {
        const payload = {
          user_id: formData.user_id, date: formData.date,
          start_time: formData.start_time, end_time: formData.end_time,
          title: formData.title, description: formData.description, address: formData.address,
          color: formData.color, priority: formData.priority,
        };
        await axios.put(`${API_URL}/api/planning/${editTask.id}`, payload, { withCredentials: true });
        toast.success('Tache modifiee');
      } else {
        if (!formData.user_ids || formData.user_ids.length === 0) return toast.error('Selectionnez au moins un salarie');
        const mode = formData.date_mode || 'single';
        const body = {
          user_ids: formData.user_ids,
          start_time: formData.start_time, end_time: formData.end_time,
          title: formData.title, description: formData.description, address: formData.address,
          color: formData.color, priority: formData.priority,
        };
        if (mode === 'range') {
          if (!formData.start_date || !formData.end_date) return toast.error('Dates de debut et fin requises');
          body.start_date = formData.start_date;
          body.end_date = formData.end_date;
          if (formData.weekdays && formData.weekdays.length > 0) body.weekdays = formData.weekdays;
        } else {
          if (!formData.date) return toast.error('Date requise');
          body.date = formData.date;
        }
        const res = await axios.post(`${API_URL}/api/planning`, body, { withCredentials: true });
        toast.success(`${res.data?.count || 1} tache(s) creee(s)`);
      }
      setShowModal(false);
      setEditTask(null);
      resetForm();
      fetchData();
      if (viewMode === 'year') fetchYearTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await axios.delete(`${API_URL}/api/planning/${taskId}`, { withCredentials: true });
      toast.success('Tache supprimee');
      fetchData();
      if (viewMode === 'year') fetchYearTasks();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteSeries = async (recurrenceGroup) => {
    try {
      const res = await axios.delete(`${API_URL}/api/planning/recurrence/${recurrenceGroup}`, { withCredentials: true });
      toast.success(`${res.data?.deleted || 0} tache(s) de la serie supprimee(s)`);
      fetchData();
      if (viewMode === 'year') fetchYearTasks();
    } catch {
      toast.error('Erreur lors de la suppression de la serie');
    }
  };

  const handleMoveTask = async (taskId, newDate, newUserId = null) => {
    try {
      const payload = { date: newDate };
      if (newUserId) payload.user_id = newUserId;
      await axios.put(`${API_URL}/api/planning/${taskId}`, payload, { withCredentials: true });
      toast.success('Tache deplacee');
      fetchData();
    } catch {
      toast.error('Erreur deplacement');
    }
  };

  const openEditModal = (task) => {
    setEditTask(task);
    setFormData({
      user_id: task.user_id, user_ids: [task.user_id], date_mode: 'single',
      date: task.date, start_date: '', end_date: '', weekdays: [],
      start_time: task.start_time, end_time: task.end_time,
      title: task.title, description: task.description || '', address: task.address || '',
      color: task.color || '#1565C0', priority: task.priority || 'normal',
    });
    setShowModal(true);
  };

  const openAddModalForCell = (employeeId, dateStr) => {
    resetForm();
    setEditTask(null);
    setFormData(prev => ({ ...prev, user_ids: [employeeId], user_id: employeeId, date: dateStr }));
    setShowModal(true);
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekNumber = getISOWeek(weekStart);

  if (loading && viewMode === 'week') {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="planning-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Planning</h1>
          <p className="page-subtitle">Gerez le planning de vos employes</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {isAdmin && (
            <label
              data-testid="include-admins-toggle"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid ' + (includeAdmins ? '#002FA7' : '#E2E2E6'), background: includeAdmins ? '#EFF6FF' : 'white', color: includeAdmins ? '#002FA7' : '#5E5F66', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
              title="Afficher aussi les administrateurs comme ressources du planning"
            >
              <input
                type="checkbox"
                checked={includeAdmins}
                onChange={(e) => setIncludeAdmins(e.target.checked)}
                style={{ marginRight: 4 }}
              />
              Inclure les admins
            </label>
          )}
          {isAdmin && (
            googleStatus.connected ? (
              <button data-testid="google-disconnect-btn" className="btn btn-secondary" onClick={handleGoogleDisconnect}
                title={`Connecte: ${googleStatus.google_email || ''}`}
                style={{ display: 'flex', alignItems: 'center', gap: 6, borderColor: '#34A853', color: '#34A853' }}>
                <GoogleLogo size={18} weight="bold" /> Google connecte
              </button>
            ) : (
              <button data-testid="google-connect-btn" className="btn btn-secondary" onClick={handleGoogleConnect}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <GoogleLogo size={18} weight="bold" /> Connecter Google Agenda
              </button>
            )
          )}
          <button data-testid="export-planning-pdf-btn" className="btn btn-secondary"
            onClick={() => window.open(`${API_URL}/api/planning/export/pdf?start_date=${weekStartStr}&end_date=${weekEndStr}`, '_blank')}>
            <FilePdf size={18} /> Export PDF
          </button>
          <button data-testid="add-task-btn" className="btn btn-primary"
            onClick={() => { resetForm(); setEditTask(null); setShowModal(true); }}>
            <Plus size={18} weight="bold" /> Ajouter une tache
          </button>
        </div>
      </div>

      {/* View Switcher */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #E2E2E6', marginBottom: '24px' }}>
        <button data-testid="view-week-btn" onClick={() => setViewMode('week')}
          style={{
            padding: '10px 24px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
            fontWeight: viewMode === 'week' ? '700' : '400',
            borderBottom: viewMode === 'week' ? '2px solid var(--primary, #002FA7)' : '2px solid transparent',
            marginBottom: '-2px', color: viewMode === 'week' ? 'var(--primary, #002FA7)' : '#5E5F66',
            display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem'
          }}>
          <ListBullets size={18} /> Vue Semaine
        </button>
        <button data-testid="view-year-btn" onClick={() => setViewMode('year')}
          style={{
            padding: '10px 24px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
            fontWeight: viewMode === 'year' ? '700' : '400',
            borderBottom: viewMode === 'year' ? '2px solid var(--primary, #002FA7)' : '2px solid transparent',
            marginBottom: '-2px', color: viewMode === 'year' ? 'var(--primary, #002FA7)' : '#5E5F66',
            display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem'
          }}>
          <CalendarDots size={18} /> Calendrier Annuel
        </button>
      </div>

      {/* ========= WEEK VIEW ========= */}
      {viewMode === 'week' && (
        <>
          {/* Week Navigation */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setWeekStart(addDays(weekStart, -7))}>
                Semaine precedente
              </button>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <button className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CalendarBlank size={18} />
                    <span style={{ fontWeight: '700', color: 'var(--primary, #002FA7)', marginRight: '4px' }} data-testid="week-number">S{weekNumber}</span>
                    Semaine du {format(weekStart, 'dd MMMM yyyy', { locale: fr })}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={weekStart}
                    onSelect={(d) => { if (d) { setWeekStart(startOfWeek(d, { weekStartsOn: 1 })); setCalendarOpen(false); } }}
                    initialFocus />
                </PopoverContent>
              </Popover>
              <button className="btn btn-secondary btn-sm" onClick={() => setWeekStart(addDays(weekStart, 7))}>
                Semaine suivante
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}>
                Aujourd'hui
              </button>
            </div>
          </div>

          {/* Google Calendar events bar (admin only, read-only external events) */}
          {isAdmin && googleStatus.connected && googleEvents.length > 0 && (
            <div className="card" style={{ marginBottom: '16px', borderLeft: '4px solid #34A853' }}>
              <div className="card-body" style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <GoogleLogo size={18} weight="bold" color="#34A853" />
                  <strong style={{ fontSize: '0.9rem' }}>Mon agenda Google ({googleEvents.length} evenement{googleEvents.length > 1 ? 's' : ''})</strong>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {googleEvents.map(ev => (
                    <a key={ev.id} href={ev.html_link} target="_blank" rel="noopener noreferrer"
                      data-testid={`google-event-${ev.id}`}
                      style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534', fontSize: '0.75rem', fontWeight: 600 }}
                      title={ev.description || ''}>
                      <span style={{ opacity: 0.7 }}>{ev.start_date}{ev.start_time ? ` ${ev.start_time}` : ''}</span>
                      <span>{ev.summary}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Planning Grid */}
          <PlanningWeekView
            weekDays={weekDays}
            employees={employees}
            tasks={tasks}
            holidaysByDate={holidaysByDate}
            dragOverCell={dragOverCell}
            setDragOverCell={setDragOverCell}
            onTaskDrop={handleMoveTask}
            onTaskClick={openEditModal}
            onAddTaskForCell={openAddModalForCell}
          />
        </>
      )}

      {/* ========= ANNUAL CALENDAR VIEW ========= */}
      {viewMode === 'year' && (
        <AnnualCalendarView
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          yearTasks={yearTasks}
          yearTaskMap={yearTaskMap}
          yearLoading={yearLoading}
          hoveredDay={hoveredDay}
          setHoveredDay={setHoveredDay}
          onDayClick={(day) => {
            setWeekStart(startOfWeek(day, { weekStartsOn: 1 }));
            setViewMode('week');
          }}
        />
      )}

      {showModal && (
        <PlanningTaskModal
          editTask={editTask}
          formData={formData}
          setFormData={setFormData}
          employees={employees}
          projects={projects}
          onClose={() => { setShowModal(false); setEditTask(null); }}
          onSubmit={handleSubmit}
          onDelete={(id) => { handleDelete(id); setShowModal(false); setEditTask(null); }}
          onDeleteSeries={(group) => { handleDeleteSeries(group); setShowModal(false); setEditTask(null); }}
        />
      )}
    </div>
  );
};

export default PlanningPage;
